import { requestJson } from '../common/http';
import { getWorkLogsByDate } from '../worklog/api';
import type { MonthWorkLog } from '../worklog/models';
import type { DayCell, ExportRow, NonWorking } from './models';

// A fully-booked working day, snapped to the 1/4-h quantum
const WORKING_DAY_CAPACITY = 7.75;

const pad2 = (n: number): string => String(n).padStart(2, '0');
const iso = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fetchHolidayNames = async (year: number): Promise<Map<string, string>> => {
    const { items } = await requestJson<{ items: { date: string; name: string }[] }>({
        method: 'GET',
        path: '/holidays',
        query: { year },
    });
    return new Map(items.map((h) => [h.date, h.name]));
};

// The unified API has no `/calendar/days` endpoint — the grid is composed
// client-side from the member's month work logs (`/work-log-entries?queryType=ByDate`),
// public holidays (`/holidays`) and the member's own days-off. Capacity is the
// working-day constant on weekdays that aren't a holiday or day-off.
// Monday-start grid, padded to whole weeks.
export const loadMonth = async (teamMemberId: string, year: number, month: number, daysOff: Set<string>): Promise<DayCell[]> => {
    const [logs, holidayNames] = await Promise.all([getWorkLogsByDate(teamMemberId, year, month), fetchHolidayNames(year)]);

    const logsByDate = new Map<string, MonthWorkLog[]>();
    for (const log of logs) {
        const forDay = logsByDate.get(log.date) ?? [];
        forDay.push(log);
        logsByDate.set(log.date, forDay);
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    // getDay() is 0=Sunday..6=Saturday; shift so Monday maps to 0 for a Monday-start grid.
    const leading = (new Date(year, month - 1, 1).getDay() + 6) % 7;
    const totalCells = Math.ceil((leading + daysInMonth) / 7) * 7;
    const gridStart = new Date(year, month - 1, 1 - leading);

    const cells: DayCell[] = [];
    for (let i = 0; i < totalCells; i += 1) {
        const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
        const date = iso(d);
        const inMonth = d.getMonth() === month - 1 && d.getFullYear() === year;
        const dayLogs = inMonth ? (logsByDate.get(date) ?? []) : [];

        const savedEntries: ExportRow[] = dayLogs.map((log) => ({
            rowDate: date,
            hours: log.hours,
            developer: '',
            department: '',
            issueId: log.workItemId,
            issueTitle: log.title,
            workItemType: log.workItemType,
            workTypeLabel: log.workType,
        }));
        const savedHours = dayLogs.reduce((sum, log) => sum + log.hours, 0);

        const weekday = d.getDay();
        let nonWorking: NonWorking | null = null;
        if (inMonth) {
            if (weekday === 0 || weekday === 6) {
                nonWorking = { kind: 'weekend' };
            } else if (holidayNames.has(date)) {
                nonWorking = { kind: 'holiday', name: holidayNames.get(date) ?? '' };
            } else if (daysOff.has(date)) {
                nonWorking = { kind: 'dayOff' };
            }
        }

        const capacity = inMonth && nonWorking == null ? WORKING_DAY_CAPACITY : 0;

        cells.push({ date, inMonth, capacity, nonWorking, savedEntries, savedHours });
    }

    return cells;
};
